import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const DEFAULT_EVENT_TIMEZONE = "America/New_York";

const COMMON_EVENT_TIMEZONES = [
  DEFAULT_EVENT_TIMEZONE,
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Puerto_Rico",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
];

/** IANA zones for the event form — no API call required. */
export function getEventTimezoneIds(): string[] {
  try {
    const intl = Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    };
    if (typeof intl.supportedValuesOf === "function") {
      return intl.supportedValuesOf("timeZone");
    }
  } catch {
    // fall through
  }
  return COMMON_EVENT_TIMEZONES;
}

export function getPreferredEventTimezoneOptions(): {
  label: string;
  value: string;
}[] {
  const all = getEventTimezoneIds();
  const preferredSet = new Set(COMMON_EVENT_TIMEZONES);
  const top = COMMON_EVENT_TIMEZONES.filter((tz) => all.includes(tz)).map(
    (tz) => ({ value: tz, label: timezoneLabel(tz) })
  );
  const rest = all
    .filter((tz) => !preferredSet.has(tz))
    .map((tz) => ({ value: tz, label: timezoneLabel(tz) }));
  return [...top, ...rest];
}

const DATE_PARSE_FORMATS = ["YYYY-MM-DD", "MMMM D, YYYY", "MMM D, YYYY"];
const TIME_PARSE_FORMATS = ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"];

function hasFormatMethod(value: unknown): value is Dayjs {
  return (
    typeof value === "object" &&
    value !== null &&
    "format" in value &&
    typeof (value as Dayjs).format === "function"
  );
}

/** Normalize Arco picker values (dayjs, string, Date) for API formatting. */
function readDayjs(value: unknown, kind: "date" | "time"): Dayjs | null {
  if (value == null || value === "") {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (hasFormatMethod(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === "string") {
    const formats = kind === "date" ? DATE_PARSE_FORMATS : TIME_PARSE_FORMATS;
    for (const fmt of formats) {
      const parsed = dayjs(value, fmt, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  return null;
}

/** Build API payload: wall-clock date/time in the selected IANA timezone. */
export function buildEventDatePayload(
  eventDate: unknown,
  eventTime: unknown,
  timezone: string
): { event_date: string; timezone: string } {
  const date = readDayjs(eventDate, "date");
  const time = readDayjs(eventTime, "time");

  if (!date) {
    throw new Error("Invalid event date");
  }
  if (!time) {
    throw new Error("Invalid event time");
  }

  const dateStr = date.format("YYYY-MM-DD");
  const timeStr = time.format("HH:mm:ss");

  if (dateStr === "Invalid Date" || timeStr === "Invalid Date") {
    throw new Error("Invalid event date or time");
  }

  return {
    event_date: `${dateStr}T${timeStr}`,
    timezone: timezone || DEFAULT_EVENT_TIMEZONE,
  };
}

/** Populate form fields when editing an existing event. */
export function parseEventForForm(
  eventDateIso: string,
  timezone?: string
): {
  eventDate: Dayjs;
  eventTime: Dayjs;
  timezone: string;
} {
  const tz = timezone || DEFAULT_EVENT_TIMEZONE;
  const instant = new Date(eventDateIso);

  const dateLabel = instant.toLocaleDateString("en-CA", { timeZone: tz });
  const [year, month, day] = dateLabel.split("-").map(Number);

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(instant);

  const hourPart = parts.find((p) => p.type === "hour")?.value ?? "12";
  const minutePart = parts.find((p) => p.type === "minute")?.value ?? "00";
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value ?? "AM";

  const hour12 = parseInt(hourPart, 10);
  let hour24 = hour12 % 12;
  if (dayPeriod.toUpperCase() === "PM") {
    hour24 += 12;
  }

  return {
    eventDate: dayjs(new Date(year, month - 1, day)),
    eventTime: dayjs().hour(hour24).minute(parseInt(minutePart, 10)).second(0),
    timezone: tz,
  };
}

export function isEventUpcoming(
  eventDateIso: string,
  timezone?: string
): boolean {
  const tz = timezone || DEFAULT_EVENT_TIMEZONE;
  const eventInstant = new Date(eventDateIso);
  const now = new Date();
  if (Number.isNaN(eventInstant.getTime())) {
    return false;
  }

  const eventDay = eventInstant.toLocaleDateString("en-CA", { timeZone: tz });
  const nowDay = now.toLocaleDateString("en-CA", { timeZone: tz });
  if (eventDay > nowDay) {
    return true;
  }
  if (eventDay < nowDay) {
    return false;
  }

  const eventMinutes = eventInstant.toLocaleTimeString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const nowMinutes = now.toLocaleTimeString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return eventMinutes >= nowMinutes;
}

/** Short zone label for display on cards (e.g. EST, MST, PDT). */
export function formatEventTimezoneAbbrev(
  timezone?: string,
  at: Date = new Date()
): string {
  const tz = timezone || DEFAULT_EVENT_TIMEZONE;
  try {
    const abbrev = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value;
    return abbrev || tz.replace(/_/g, " ");
  } catch {
    return tz.replace(/_/g, " ");
  }
}

export function formatEventDateTime(
  eventDateIso: string,
  timezone?: string
): string {
  const tz = timezone || DEFAULT_EVENT_TIMEZONE;
  const instant = new Date(eventDateIso);
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(instant);
  const abbrev = formatEventTimezoneAbbrev(tz, instant);
  return `${formatted} ${abbrev}`;
}

export function timezoneLabel(tz: string): string {
  try {
    const short = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longGeneric",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    return short ? `${tz.replace(/_/g, " ")} (${short})` : tz.replace(/_/g, " ");
  } catch {
    return tz.replace(/_/g, " ");
  }
}
